/**
 * The search box, and the five suggestions under it.
 *
 * ACCESSIBILITY IS THE POINT OF THIS WHOLE MIGRATION, so this is a real
 * combobox and not a text input with a floating div of clickable rows. It
 * implements the WAI-ARIA combobox pattern: the input owns the listbox, the
 * active option is named by aria-activedescendant rather than focused, arrow
 * keys move through options without the caret leaving the field, Enter opens
 * the active one, Escape closes. A screen reader announces the option count
 * and each option as it becomes active; the old canvas renderer could announce none
 * of this, which is why we are here.
 *
 * The dropdown shows five, however many matched — PolicyStat's behaviour, and
 * the right one: a type-ahead that refuses to suggest anything until you have
 * narrowed is a type-ahead that never helps.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounced } from './useDebounced';
import { useInstantSearch, INSTANT_HITS, MIN_QUERY_LENGTH } from './useSearch';
import { Highlight } from './HighlightText';
import styles from './SearchBox.module.css';
import { SEARCH_QUERY_MAX } from '../../types/fieldLimits';

export interface SearchBoxProps {
  /** Placeholder, so the policies page and the shell can word it differently. */
  placeholder?: string;
  /** Pre-fill, used when the results page mounts with a query already set. */
  initialQuery?: string;
  autoFocus?: boolean;
}

export function resultsHref(query: string): string {
  return `/search?q=${encodeURIComponent(query.trim())}`;
}

export function SearchBox({
  placeholder = 'Search policies',
  initialQuery = '',
  autoFocus = false,
}: SearchBoxProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialQuery);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const statusId = useId();

  // Typing is faster than the network, and every keystroke is a billable
  // search. 180ms is below the threshold where a person notices a delay and
  // well above the gap between two keystrokes.
  const debounced = useDebounced(query, 180);
  const { data, isFetching } = useInstantSearch(debounced, open);

  const hits = useMemo(() => data?.hits.slice(0, INSTANT_HITS) ?? [], [data]);
  const long = query.trim().length >= MIN_QUERY_LENGTH;

  // "See all" is one row past the last suggestion, so Down from the bottom
  // reaches it rather than wrapping past the thing most people want.
  const seeAllIndex = hits.length;
  const optionCount = hits.length + (hits.length > 0 ? 1 : 0);

  // A new search starts with nothing highlighted. Reset while rendering, when
  // the query changes, rather than in an effect that draws the old row first.
  const [searched, setSearched] = useState(debounced);
  if (searched !== debounced) {
    setSearched(debounced);
    setActive(-1);
  }

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  function go(href: string) {
    setOpen(false);
    setActive(-1);
    navigate(href);
  }

  function openActive() {
    const hit = active >= 0 ? hits[active] : undefined;
    if (hit) {
      go(`/policy/${hit.policyId}`);
    } else if (long) {
      go(resultsHref(query));
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) setOpen(true);
        if (optionCount > 0) setActive((i) => (i + 1) % optionCount);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (optionCount > 0) setActive((i) => (i <= 0 ? optionCount - 1 : i - 1));
        break;
      case 'Enter':
        // Let the form submit when nothing is active, so Enter on a typed
        // query goes to the results page.
        if (active >= 0) e.preventDefault();
        openActive();
        break;
      case 'Escape':
        // First Escape closes the list, a second clears the box — the
        // behaviour the pattern specifies, and what people expect from a
        // browser's own search field.
        if (open) {
          setOpen(false);
          setActive(-1);
        } else if (query) {
          setQuery('');
        }
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  }

  const showList = open && long && (hits.length > 0 || (!isFetching && Boolean(data)));

  return (
    <div className={styles.root} ref={rootRef}>
      <form
        role="search"
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (long) go(resultsHref(query));
        }}
      >
        <div className={styles.field}>
          <svg className={styles.icon} width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
            <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
            <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>

          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            maxLength={SEARCH_QUERY_MAX}
            placeholder={placeholder}
            aria-label={placeholder}
            value={query}
            autoFocus={autoFocus}
            autoComplete="off"
            spellCheck={false}
            role="combobox"
            aria-expanded={showList}
            aria-controls={showList ? listId : undefined}
            aria-autocomplete="list"
            aria-activedescendant={
              showList && active >= 0 ? `${listId}-opt-${active}` : undefined
            }
            aria-describedby={statusId}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
          />

          {query && (
            <button
              type="button"
              className={styles.clear}
              onClick={() => {
                setQuery('');
                setActive(-1);
                inputRef.current?.focus();
              }}
            >
              <span aria-hidden="true">×</span>
              <span className={styles.srOnly}>Clear search</span>
            </button>
          )}
        </div>
      </form>

      {/* Announced, not drawn. A sighted user sees the list; a screen reader
          user needs to be told how many came back. */}
      <div className={styles.srOnly} role="status" aria-live="polite" id={statusId}>
        {!long
          ? ''
          : isFetching
            ? 'Searching'
            : data
              ? `${data.totalHits} ${data.totalHits === 1 ? 'policy' : 'policies'} found`
              : ''}
      </div>

      {showList && (
        <ul className={styles.list} id={listId} role="listbox" aria-label="Policy suggestions">
          {hits.length === 0 && (
            <li className={styles.empty} role="presentation">
              Nothing matched “{query.trim()}”.
            </li>
          )}

          {hits.map((hit, i) => (
            <li
              key={hit.objectID}
              id={`${listId}-opt-${i}`}
              role="option"
              aria-selected={i === active}
              className={i === active ? `${styles.option} ${styles.active}` : styles.option}
              onPointerEnter={() => setActive(i)}
              onClick={() => go(`/policy/${hit.policyId}`)}
            >
              <span className={styles.optionTitle}>
                <Highlight value={hit._highlightResult?.title?.value} fallback={hit.title} />
              </span>
              <span className={styles.optionMeta}>
                {[hit.policyNumber, hit.departmentName].filter(Boolean).join(' · ')}
              </span>
            </li>
          ))}

          {hits.length > 0 && data && (
            <li
              id={`${listId}-opt-${seeAllIndex}`}
              role="option"
              aria-selected={active === seeAllIndex}
              className={
                active === seeAllIndex ? `${styles.seeAll} ${styles.active}` : styles.seeAll
              }
              onPointerEnter={() => setActive(seeAllIndex)}
              onClick={() => go(resultsHref(query))}
            >
              See all {data.totalHits.toLocaleString()}{' '}
              {data.totalHits === 1 ? 'result' : 'results'} →
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
